/** Supernova's capsules (docs/spec/03 §2): a small pod in its kind's colour, with its letter. */

import type { CapsuleKind } from '../../sim/types';
import { makeCanvas } from '../canvas';
import { css, drawText } from '../font';
import { CYAN, LIME, MAGENTA, ORANGE, PINK, SKY, WHITE, YELLOW } from '../palette';
import type { Screen } from '../screen';

export interface CapsuleLook {
  readonly letter: string;
  readonly colour: number;
  /** The name shown when it is taken. */
  readonly name: string;
}

export const CAPSULE_LOOK: Readonly<Record<CapsuleKind, CapsuleLook>> = {
  rapid: { letter: 'R', colour: ORANGE, name: 'RAPID' },
  spread: { letter: 'S', colour: LIME, name: 'SPREAD' },
  pierce: { letter: 'P', colour: YELLOW, name: 'PIERCE' },
  shield: { letter: 'H', colour: SKY, name: 'SHIELD' },
  chrono: { letter: 'C', colour: MAGENTA, name: 'CHRONO' },
  nova: { letter: 'N', colour: CYAN, name: 'NOVA' },
  life: { letter: '+', colour: PINK, name: '1UP' },
};

/** Rows of the 14 x 12 pod: `#` rim, `o` fill, `-` shine. */
const POD = [
  '...########...',
  '.##--------##.',
  '.#-oooooooo-#.',
  '#-oooooooooo-#',
  '#oooooooooooo#',
  '#oooooooooooo#',
  '#oooooooooooo#',
  '#oooooooooooo#',
  '#oooooooooooo#',
  '.#oooooooooo#.',
  '.##oooooooo##.',
  '...########...',
];

const cache = new Map<string, HTMLCanvasElement>();

/** A capsule as a small picture, made the first time it is wanted: the pod is 168 pixels, too many to fill each frame. */
function picture(kind: CapsuleKind, bright: boolean): HTMLCanvasElement {
  const key = `${kind}/${bright ? 1 : 0}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const look = CAPSULE_LOOK[kind];
  const canvas = makeCanvas(14, POD.length);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  POD.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const ch = row[x];
      if (ch === '.') continue;
      ctx.fillStyle = css(ch === '#' ? (bright ? WHITE : look.colour) : ch === '-' ? WHITE : '#101020');
      ctx.fillRect(x, y, 1, 1);
    }
  });
  drawText(ctx, look.letter, 3, 3, look.colour);
  cache.set(key, canvas);
  return canvas;
}

/** Draw a capsule centred on (cx, cy); `t` (ticks) makes its rim pulse. */
export function drawCapsule(screen: Screen, kind: CapsuleKind, cx: number, cy: number, t = 0): void {
  screen.image(picture(kind, Math.floor(t / 8) % 2 === 0), Math.round(cx) - 7, Math.round(cy) - 6);
}

/** Make every capsule's pictures now (at load) rather than at the moment the first one falls. */
export function warmCapsules(): void {
  for (const kind of Object.keys(CAPSULE_LOOK) as CapsuleKind[]) {
    picture(kind, true);
    picture(kind, false);
  }
}
